import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Hammer, Star, Award, CheckCircle2, ExternalLink } from "lucide-react";
import { marketplaceApi } from "@/services/api";
import { fmtMoney, fmtDate, apiError } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function PortalTenders() {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await marketplaceApi.portalLeads(); setLeads(data || []); }
    catch (e) { toast.error(apiError(e, "Could not load your remedial works")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const award = async (lead, bid) => {
    if (!window.confirm(`Award these works to ${bid.contractor?.name || bid.contractor_name} for ${fmtMoney(bid.amount)}?`)) return;
    setBusy(bid.id);
    try { await marketplaceApi.awardBid(lead.id, bid.id); toast.success("Contractor appointed"); await load(); }
    catch (e) { toast.error(apiError(e, "Could not award the works")); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="portal-tenders">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Remedial works — contractor quotes</h1>
        <p className="mt-1 text-sm text-slate-500">Compare quotes from vetted fire-door maintenance companies and appoint one.</p>
      </div>

      {leads.length === 0 && (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="portal-tenders-empty">
          No remedial works are out to quote right now. When your inspector puts remedial works out to the contractor network, the quotes will appear here.
        </Card>
      )}

      {leads.map((lead) => {
        const awarded = lead.status === "awarded";
        return (
          <Card key={lead.id} className="p-5" data-testid={`portal-tender-${lead.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold text-slate-800">{lead.title || "Remedial works"}</h2>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {lead.building_name || "Building"} · {lead.building_postcode || "—"}</p>
              </div>
              {awarded
                ? <Badge className="border-0 bg-emerald-100 text-emerald-800">Appointed · {fmtMoney(lead.awarded_amount)}</Badge>
                : <Badge className="border-0 bg-blue-100 text-blue-700">Out to quote</Badge>}
            </div>

            {(lead.scope || []).length > 0 && (
              <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/70 p-3">
                <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Hammer className="h-3.5 w-3.5" /> Scope of works</p>
                <ul className="space-y-0.5 text-sm text-slate-700">
                  {lead.scope.slice(0, 12).map((w, i) => (<li key={i}>• {w.door} — {w.description}{w.quantity > 1 ? ` ×${w.quantity}` : ""}</li>))}
                </ul>
              </div>
            )}

            <div className="mt-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">{lead.bids.length} quote{lead.bids.length === 1 ? "" : "s"} received</p>
              {lead.bids.length === 0 && <p className="text-sm text-slate-400">No quotes yet — contractors in your area have been notified.</p>}
              {lead.bids.map((bid) => {
                const isWinner = bid.status === "won";
                return (
                  <div key={bid.id} className={`rounded-lg border p-4 ${isWinner ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200"}`} data-testid={`portal-bid-${bid.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800">{bid.contractor?.name || bid.contractor_name}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                          {typeof bid.contractor?.google_rating === "number" && (
                            bid.contractor?.google_reviews_url ? (
                              <a href={bid.contractor.google_reviews_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-amber-600 hover:underline" data-testid={`portal-bid-rating-${bid.id}`}>
                                <Star className="h-3.5 w-3.5 fill-amber-400" /> {bid.contractor.google_rating.toFixed(1)}{typeof bid.contractor.google_reviews_count === "number" ? ` (${bid.contractor.google_reviews_count})` : ""}
                              </a>
                            ) : (
                              <span className="flex items-center gap-1 text-amber-600" data-testid={`portal-bid-rating-${bid.id}`}>
                                <Star className="h-3.5 w-3.5 fill-amber-400" /> {bid.contractor.google_rating.toFixed(1)}{typeof bid.contractor.google_reviews_count === "number" ? ` (${bid.contractor.google_reviews_count})` : ""}
                              </span>
                            )
                          )}
                          {(bid.contractor?.accreditations || []).slice(0, 3).map((a) => (<span key={a} className="rounded bg-slate-100 px-1.5 py-0.5">{a}</span>))}
                          {(bid.contractor?.qualifications || []).slice(0, 2).map((q) => (<span key={q} className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">{q}</span>))}
                          {bid.contractor?.website && (
                            <a href={bid.contractor.website} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 text-primary hover:underline">Website <ExternalLink className="h-3 w-3" /></a>
                          )}
                        </div>
                        {bid.message && <p className="mt-2 text-sm text-slate-600">“{bid.message}”</p>}
                        {bid.proposed_start && <p className="mt-1 text-xs text-slate-400">Earliest start {fmtDate(bid.proposed_start)}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">{fmtMoney(bid.amount)}</p>
                        <p className="text-xs text-slate-400">ex VAT</p>
                        {!awarded && (
                          <Button size="sm" className="mt-2 bg-red-600 hover:bg-red-700" disabled={busy === bid.id} onClick={() => award(lead, bid)} data-testid={`portal-award-${bid.id}`}>
                            {busy === bid.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Award className="mr-1.5 h-4 w-4" />} Appoint
                          </Button>
                        )}
                        {isWinner && <p className="mt-2 flex items-center justify-end gap-1 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Appointed</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
